import React, { useEffect } from 'react';
import cx from 'classnames';
import { Button, Form, Input } from 'antd';
import ProviderEmailInput from 'components/form/ProviderEmailInput';
import { checkRenderingProviderEmail } from 'api/insuranceBilling';
import type { CheckRenderingEmailVo } from 'types/insuranceBilling';
import SectionHeader from '../../components/SectionHeader';
import InfoTip from '../../components/InfoTip';
import OptLabel from '../../components/OptLabel';
import YesNoToggle from '../../components/YesNoToggle';
import CardRadioGroup from '../../components/CardRadioGroup';
import AddressBlock from '../../components/AddressBlock';
import HiddenFormField from '../../components/HiddenFormField';
import NpiRegistryBox from '../../components/NpiRegistryBox';
import { billingAddressSummary, npiUnverified } from '../../utils';
import s from '../../s.module.less';
import sc from './s.module.less';

// rendering provider 必须是 Individual（NPI-1）：类型不符时的专用文案，
// 比 NpiRegistryBox 的通用文案多说明「这里要的是 rendering provider 的 NPI」
const RENDERING_TYPE_ERROR =
    'This is an Organization NPI (NPI-2). Please enter an Individual NPI (NPI-1) for the rendering provider.';

const InfoIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" fill="currentColor" />
        <circle cx="12" cy="8" r="1.25" fill="#fff" />
        <path d="M12 11.5v5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const normalizeNpi = (v?: string) => (v || '').replace(/\D/g, '').slice(0, 10);

// rendering provider 名单专用 email 预检（与 activate 同一套判定口径；团队邀请口径会误判本诊所已有医生）。
// 模块级稳定引用：ProviderEmailInput 的缓存按检查函数维度隔离
const checkRenderingEmail = async (email: string) => {
    const res: any = await checkRenderingProviderEmail(email);
    const body = res?.data;
    if (res?.error || body?.code !== 200 || !body?.data) return null;
    const vo: CheckRenderingEmailVo = body.data;
    return { addable: !!vo.addable, message: vo.message || undefined };
};

// NPI + PTAN 输入行（same / diff 两分支共用；same 分支无 email 且不采集 PTAN——
// same as billing 即本人，PTAN 沿用 billing 侧已有信息，无需重复填写）
function NpiPtanRow({ index, showPtan = true }: { index: number; showPtan?: boolean }) {
    return (
        <div className={s.wizRow}>
            <Form.Item
                name={['rendering', 'providers', index, 'npi']}
                label={
                    <span>
                        NPI
                        <InfoTip title="This is the individual provider's NPI, not the organization NPI." />
                    </span>
                }
                normalize={normalizeNpi}
            >
                <Input size="large" inputMode="numeric" maxLength={10} placeholder="10 digit individual NPI" />
            </Form.Item>
            {showPtan && (
                <Form.Item
                    name={['rendering', 'providers', index, 'ptan']}
                    label={
                        <span>
                            <OptLabel>PTAN</OptLabel>
                            <InfoTip title="Required for Medicare claims. Each provider has their own PTAN assigned by their Medicare contractor." />
                        </span>
                    }
                >
                    <Input size="large" placeholder="PTAN" />
                </Form.Item>
            )}
        </div>
    );
}

// 单个 provider 块（原型 providerBlock）：仅 Provider 1 有「same as billing provider」选项，
// 后加的 provider 一律是独立的人（直接编辑身份字段）
function ProviderBlock({ index, canRemove, onRemove }: { index: number; canRemove: boolean; onRemove: () => void }) {
    const form = Form.useFormInstance();
    const isFirst = index === 0;
    const sameAsBilling = Form.useWatch(['rendering', 'providers', index, 'sameAsBilling'], form);
    const addressChoice = Form.useWatch(['rendering', 'providers', index, 'addressChoice'], form);
    // billing / address 属于其它步骤（字段已卸载），useWatch 初始值读不到，须用 getFieldsValue(true)
    const fullValues = form.getFieldsValue(true) || {};
    const { billing } = fullValues;
    const billingAddress = fullValues.address;

    const same = isFirst && sameAsBilling === 'yes';
    const diff = isFirst ? sameAsBilling === 'no' : true;

    // same=yes 且 billing NPI 是 Individual：本行 NPI 从 billing 预填（连同已查好的
    // npiType/taxonomy，避免 NpiRegistryBox 重查闪烁）；只填空值，用户改过不覆盖。
    // 切回 No, different provider 时，若仍是预填的 billing NPI 原值则清掉——
    // 那是 billing 本人的 NPI，不能留在「不同的人」的行里
    useEffect(() => {
        if (!isFirst) return;
        const ns = ['rendering', 'providers', index];
        const b = form.getFieldValue(['billing']) || {};
        if (same) {
            if (b.npiType !== 'Type 1' || npiUnverified(b)) return;
            if (form.getFieldValue([...ns, 'npi'])) return;
            form.setFieldValue([...ns, 'npi'], b.npi);
            form.setFieldValue([...ns, 'npiType'], b.npiType);
            form.setFieldValue([...ns, 'taxonomy'], b.taxonomy);
            const options = b.taxonomyOptions || [];
            if (options.length) form.setFieldValue([...ns, 'taxonomyOptions'], options);
            form.setFieldValue([...ns, 'npiPrefilled'], true);
        } else if (form.getFieldValue([...ns, 'npiPrefilled'])) {
            if (form.getFieldValue([...ns, 'npi']) === b.npi) {
                form.setFieldValue([...ns, 'npi'], undefined);
                form.setFieldValue([...ns, 'npiType'], undefined);
                form.setFieldValue([...ns, 'taxonomy'], undefined);
                form.setFieldValue([...ns, 'taxonomyOptions'], undefined);
            }
            form.setFieldValue([...ns, 'npiPrefilled'], false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [same]);

    // same=yes 时 NPI 行下方的说明：billing 为 Individual 说明预填来源，为 Organization 提示必须另填个人 NPI
    let sameNpiHint: React.ReactNode = null;
    if (same && billing?.npiType === 'Type 1') {
        sameNpiHint =
            'Prefilled from the Billing step, because you bill under your own individual NPI. Edit it if the ' +
            'service is rendered under a different NPI.';
    } else if (same && billing?.npiType === 'Type 2') {
        sameNpiHint = (
            <>
                Your practice bills under a group NPI ({billing?.npi}). A group NPI cannot be a rendering NPI, so enter{' '}
                <strong>your own</strong> individual NPI (NPI-1) here.
            </>
        );
    }

    return (
        <div className={sc.providerBlock}>
            <div className={sc.providerHead}>
                <div className={sc.providerTitle}>Provider {index + 1}</div>
                {canRemove && (
                    <Button danger size="small" className={sc.providerRemove} onClick={onRemove}>
                        Remove
                    </Button>
                )}
            </div>

            {isFirst && (
                <Form.Item name={['rendering', 'providers', index, 'sameAsBilling']} label="Same as billing provider?">
                    <YesNoToggle yesLabel="Yes, same person" noLabel="No, different provider" />
                </Form.Item>
            )}

            {same && (
                <>
                    <div className={s.wizRow}>
                        <div>
                            <p className={sc.fieldLabel}>First name</p>
                            <div className={sc.readonlyField}>{billing?.firstName || 'From billing provider'}</div>
                        </div>
                        <div>
                            <p className={sc.fieldLabel}>Last name</p>
                            <div className={sc.readonlyField}>{billing?.lastName || 'From billing provider'}</div>
                        </div>
                    </div>
                    <NpiPtanRow index={index} showPtan={false} />
                    {sameNpiHint && <p className={sc.npiHint}>{sameNpiHint}</p>}
                    <NpiRegistryBox
                        ns={['rendering', 'providers', index]}
                        expectType="Type 1"
                        typeErrorText={RENDERING_TYPE_ERROR}
                    />
                </>
            )}

            {diff && (
                <>
                    <div className={s.wizRow}>
                        <Form.Item name={['rendering', 'providers', index, 'firstName']} label="First name">
                            <Input size="large" placeholder="First name" />
                        </Form.Item>
                        <Form.Item name={['rendering', 'providers', index, 'lastName']} label="Last name">
                            <Input size="large" placeholder="Last name" />
                        </Form.Item>
                    </div>
                    <ProviderEmailInput
                        name={['rendering', 'providers', index, 'email']}
                        label={
                            <span>
                                Email
                                <InfoTip title="Used to identify this provider in your practice. Each provider needs their own email." />
                            </span>
                        }
                        size="large"
                        checkEmail={checkRenderingEmail}
                        // 本地查重范围：同列表其它 provider 已填的 email
                        getSiblingEmails={() =>
                            ((form.getFieldValue(['rendering', 'providers']) || []) as Record<string, any>[])
                                .filter((_, i) => i !== index)
                                .map((p) => p?.email)
                        }
                        // 查重结果写入 emailStatus 辅助字段，供 Continue 硬拦截与 Review 校验同步读取
                        onStatusChange={(status) =>
                            form.setFieldValue(['rendering', 'providers', index, 'emailStatus'], status)
                        }
                    />
                    <NpiPtanRow index={index} />
                    <NpiRegistryBox
                        ns={['rendering', 'providers', index]}
                        expectType="Type 1"
                        typeErrorText={RENDERING_TYPE_ERROR}
                    />
                </>
            )}

            {(same || diff) && (
                <>
                    <Form.Item name={['rendering', 'providers', index, 'addressChoice']} label="Provider address">
                        <CardRadioGroup
                            horizontal
                            options={[
                                {
                                    value: 'same',
                                    title: 'Same as billing address',
                                    desc: billingAddressSummary(billingAddress),
                                },
                                { value: 'different', title: 'Different address' },
                            ]}
                        />
                    </Form.Item>
                    {addressChoice === 'different' && (
                        <div className={sc.providerAddr}>
                            <AddressBlock ns={['rendering', 'providers', index, 'addr']} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// Step 5 — Rendering providers：可重复 provider 列表。
// 不用 Form.List：List 会给内部 Form.Item 的 name 自动加前缀，与本页「绝对路径命名空间」冲突
// （NpiRegistryBox / AddressBlock 都按绝对路径读写），改为 useWatch + setFieldValue 手动管理数组
export default function StepRenderingProviders() {
    const form = Form.useFormInstance();
    // useWatch 只作重渲染触发器：其返回值会被已挂载子字段污染（删除的 index 被重新并入快照），
    // 实际数组从原始 store 读取
    Form.useWatch(['rendering', 'providers'], form);
    const providers: Record<string, any>[] = form.getFieldValue(['rendering', 'providers']) || [];
    // 表单里还没有数据时也渲染一行空 provider（与原型一致）
    const count = Math.max(providers.length, 1);

    const addProvider = () => {
        const current = form.getFieldValue(['rendering', 'providers']) || [{}];
        form.setFieldValue(['rendering', 'providers'], [...current, { sameAsBilling: 'no' }]);
    };

    const removeProvider = (index: number) => {
        const current: any[] = form.getFieldValue(['rendering', 'providers']) || [];
        form.setFieldValue(
            ['rendering', 'providers'],
            current.filter((_, i) => i !== index),
        );
    };

    return (
        <>
            <div className={cx(s.callout, sc.edu)}>
                <InfoIcon />
                <span>
                    Add every provider whose claims are billed under this practice. If you bill under a{' '}
                    <strong>group NPI</strong>, add your whole team here. If you bill under your{' '}
                    <strong>individual NPI</strong>, you are the only rendering provider.
                </span>
            </div>
            <div className={s.wizSection} data-wiz-section>
                <SectionHeader title="Provider details" />
                {/* 注册 providers 父路径：本版本 useWatch 只见已注册字段，新增行（尚无注册字段）
                    必须靠父路径注册才能被 useWatch 看到，否则 Add 后列表不刷新 */}
                <Form.Item name={['rendering', 'providers']} noStyle>
                    <HiddenFormField />
                </Form.Item>
                {Array.from({ length: count }, (_, index) => (
                    // 数组按 index 读写、无独立身份，index 作 key 即可
                    // eslint-disable-next-line react/no-array-index-key
                    <ProviderBlock
                        key={index}
                        index={index}
                        canRemove={count > 1}
                        onRemove={() => removeProvider(index)}
                    />
                ))}
                <Button className={s.addBtn} onClick={addProvider}>
                    + Add another provider
                </Button>
            </div>
        </>
    );
}
